import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Mail, Sparkles, CheckCircle2, PencilOff, AlertTriangle } from 'lucide-react';
import PrinceGeoffrey from '../Chat/PrinceGeoffrey';

const PROGRESS_RING_RADIUS = 44;
const PROGRESS_RING_CIRCUMFERENCE = 2 * Math.PI * PROGRESS_RING_RADIUS;

const PHASE_TO_STEP_INDEX = {
  reading: 0,
  triaging: 1,
  applying: 2,
  complete: 2,
};

const PROCESSING_STEPS = [
  { id: 'reading', label: 'Reading', icon: Mail },
  { id: 'triaging', label: 'Triaging', icon: Sparkles },
  { id: 'applying', label: 'Applying', icon: CheckCircle2 },
];

const getProcessingPhase = ({
  totalEmails,
  appliedCount,
  jobsTotal,
  jobsCompleted,
  isProcessingEvents,
}) => {
  if (totalEmails > 0 && appliedCount >= totalEmails) {
    return 'complete';
  }
  if (appliedCount > 0 || isProcessingEvents) {
    return 'applying';
  }
  if (jobsTotal > 0) {
    return 'triaging';
  }
  return 'reading';
};

const getProgressPercent = (phase, { jobsTotal, jobsCompleted, totalEmails, appliedCount, triagingCreep }) => {
  switch (phase) {
    case 'reading':
      return 8;
    case 'triaging': {
      if (jobsTotal === 0) return 12;
      const jobRatio = jobsCompleted > 0
        ? jobsCompleted / jobsTotal
        : triagingCreep;
      return 12 + Math.round(jobRatio * 58);
    }
    case 'applying':
      if (totalEmails === 0) return 82;
      return 70 + Math.round((appliedCount / totalEmails) * 30);
    default:
      return 100;
  }
};

const formatEta = (seconds) => {
  if (seconds < 10) return 'A few seconds left';
  if (seconds < 60) return `About ${seconds} seconds left`;
  const minutes = Math.ceil(seconds / 60);
  return minutes === 1 ? 'About 1 minute left' : `About ${minutes} minutes left`;
};

const useTriagingCreep = (phase, jobsCompleted, jobsTotal) => {
  const startedAtRef = useRef(Date.now());
  const [creep, setCreep] = useState(0);

  useEffect(() => {
    startedAtRef.current = Date.now();
    setCreep(0);
  }, [phase, jobsTotal]);

  useEffect(() => {
    if (phase !== 'triaging' || jobsCompleted > 0 || jobsTotal === 0) {
      return undefined;
    }

    const interval = setInterval(() => {
      const elapsed = (Date.now() - startedAtRef.current) / 1000;
      setCreep(Math.min(0.9, elapsed / 40));
    }, 400);

    return () => clearInterval(interval);
  }, [phase, jobsCompleted, jobsTotal]);

  return creep;
};

const useAdaptiveEta = (progressPercent) => {
  const startedAtRef = useRef(null);
  const [etaSeconds, setEtaSeconds] = useState(null);

  useEffect(() => {
    if (startedAtRef.current === null) {
      startedAtRef.current = Date.now();
    }
  }, []);

  useEffect(() => {
    if (progressPercent < 15 || !startedAtRef.current) {
      setEtaSeconds(null);
      return;
    }

    const elapsed = (Date.now() - startedAtRef.current) / 1000;
    const rate = progressPercent / 100;
    const estimatedTotal = elapsed / rate;
    const remaining = Math.max(0, estimatedTotal - elapsed);

    setEtaSeconds(remaining >= 2 ? Math.round(remaining) : null);
  }, [progressPercent]);

  return etaSeconds;
};

const EmailProcessingOverlay = ({
  currentEmailProcessingIndex = 0,
  totalEmails = 0,
  isProcessingEvents = false,
  readOnlyMode = false,
}) => {
  const emailJobs = useSelector((state) => state.emailJobs);

  const appliedCount = currentEmailProcessingIndex;
  const jobsTotal = emailJobs.total || 0;
  const jobsFailed = emailJobs.failed || 0;
  // Progress tracks jobs that have SETTLED, not just succeeded — a failed
  // batch must still advance the bar or the run appears to hang, but it must
  // not be counted as completed or the user is told it worked.
  const jobsCompleted = (emailJobs.completed || 0) + jobsFailed;

  const phase = useMemo(() => getProcessingPhase({
    totalEmails,
    appliedCount,
    jobsTotal,
    jobsCompleted,
    isProcessingEvents,
  }), [totalEmails, appliedCount, jobsTotal, jobsCompleted, isProcessingEvents]);

  const triagingCreep = useTriagingCreep(phase, jobsCompleted, jobsTotal);

  const progressPercent = useMemo(() => getProgressPercent(phase, {
    jobsTotal,
    jobsCompleted,
    totalEmails,
    appliedCount,
    triagingCreep,
  }), [phase, jobsTotal, jobsCompleted, totalEmails, appliedCount, triagingCreep]);

  const resolvedStepIndex = PHASE_TO_STEP_INDEX[phase] ?? 0;

  const etaSeconds = useAdaptiveEta(progressPercent);
  const strokeOffset = PROGRESS_RING_CIRCUMFERENCE - (progressPercent / 100) * PROGRESS_RING_CIRCUMFERENCE;
  const isIndeterminate = phase === 'reading';

  const statusLabel = phase === 'reading'
    ? 'Preparing'
    : phase === 'triaging'
      ? 'Triaging'
      : 'Applying';

  return (
    <div className="absolute inset-0 z-10 flex items-start justify-center p-4 sm:p-6 backdrop-blur-[2px] bg-slate-900/10">
      <div
        className="sticky top-24 w-full max-w-md rounded-2xl border border-slate-200/80 bg-white/95 shadow-2xl shadow-slate-300/30 px-6 py-7"
        role="status"
        aria-live="polite"
        aria-busy="true"
      >
        <div className="flex flex-col items-center text-center">
          <div className="relative mb-5 flex h-28 w-28 items-center justify-center">
            <svg
              className="absolute inset-0 h-full w-full -rotate-90"
              viewBox="0 0 100 100"
              aria-hidden="true"
            >
              <circle
                cx="50"
                cy="50"
                r={PROGRESS_RING_RADIUS}
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                className="text-slate-100"
              />
              <circle
                cx="50"
                cy="50"
                r={PROGRESS_RING_RADIUS}
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeDasharray={PROGRESS_RING_CIRCUMFERENCE}
                strokeDashoffset={isIndeterminate ? PROGRESS_RING_CIRCUMFERENCE * 0.2 : strokeOffset}
                className={`text-sky-500 transition-all duration-700 ease-out ${isIndeterminate ? 'animate-pulse' : ''}`}
              />
            </svg>
            <div className="relative z-10 rounded-full bg-slate-50 p-1 ring-4 ring-white">
              <PrinceGeoffrey size={72} className="mb-0" />
            </div>
          </div>

          {totalEmails > 0 && (
            <div className="mb-1 flex flex-col items-center gap-0.5">
              {phase === 'applying' ? (
                <div className="flex items-baseline gap-1.5">
                  <span className="text-4xl font-semibold tabular-nums text-slate-800">
                    {appliedCount}
                  </span>
                  <span className="text-lg font-medium text-slate-400">/</span>
                  <span className="text-lg font-medium tabular-nums text-slate-500">
                    {totalEmails}
                  </span>
                </div>
              ) : (
                <span className="text-4xl font-semibold tabular-nums text-slate-800">
                  {totalEmails}
                </span>
              )}
              <span className="text-sm font-medium text-slate-500">
                {phase === 'applying'
                  ? 'emails updated'
                  : phase === 'triaging' && jobsTotal > 1
                    ? `emails · batch ${Math.min(jobsCompleted + 1, jobsTotal)} of ${jobsTotal}`
                    : 'emails in queue'}
              </span>
            </div>
          )}

          <p className="mb-4 text-xs font-semibold uppercase tracking-[0.2em] text-sky-600">
            {statusLabel}
          </p>

          <div className="mb-5 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
            <div
              className={`h-full rounded-full bg-gradient-to-r from-sky-400 to-sky-600 transition-all duration-700 ease-out ${
                isIndeterminate ? 'w-1/4 animate-pulse' : ''
              }`}
              style={isIndeterminate ? undefined : { width: `${Math.max(progressPercent, 6)}%` }}
            />
          </div>

          <div className="mb-4 flex w-full items-center justify-between gap-2 px-1">
            {PROCESSING_STEPS.map((step, index) => {
              const Icon = step.icon;
              const isActive = index === resolvedStepIndex;
              const isComplete = index < resolvedStepIndex || phase === 'complete';

              return (
                <div
                  key={step.id}
                  className={`flex flex-1 flex-col items-center gap-1.5 transition-colors duration-300 ${
                    isActive ? 'text-sky-600' : isComplete ? 'text-emerald-500' : 'text-slate-300'
                  }`}
                >
                  <div
                    className={`flex h-8 w-8 items-center justify-center rounded-full border transition-all duration-300 ${
                      isActive
                        ? 'border-sky-200 bg-sky-50 shadow-sm'
                        : isComplete
                          ? 'border-emerald-100 bg-emerald-50'
                          : 'border-slate-100 bg-slate-50'
                    }`}
                  >
                    <Icon size={15} strokeWidth={isActive ? 2.25 : 2} />
                  </div>
                  <span className="text-[11px] font-medium tracking-wide uppercase">
                    {step.label}
                  </span>
                </div>
              );
            })}
          </div>

          {jobsFailed > 0 && (
            <div className="mt-1 inline-flex items-center gap-1.5 rounded-full border border-red-200 bg-red-50 px-3 py-1 text-xs font-medium text-red-800">
              <AlertTriangle size={12} />
              {jobsFailed === 1
                ? '1 batch failed — some emails were not triaged'
                : `${jobsFailed} batches failed — some emails were not triaged`}
            </div>
          )}

          {etaSeconds !== null && jobsFailed === 0 && (
            <p className="text-sm text-slate-500">
              {formatEta(etaSeconds)}
            </p>
          )}

          {readOnlyMode && (
            <div className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-medium text-amber-800">
              <PencilOff size={12} />
              Preview only — inbox unchanged
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailProcessingOverlay;
